import React, { useCallback, forwardRef, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import trimMask from '@meta-awesome/functions/src/trimMask'
import PropTypes from 'prop-types'
import uuid from 'short-uuid'

import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import { reloadSchema } from '@galo-monitor/forms'
import companySchema from '@galo-monitor/forms/schemas/root/company.schema'
import I18n, { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CompanyActions } from '@galo-monitor/stores/company'
import Checkbox from '@galo-monitor/web-components/Checkbox'
import Form from '@galo-monitor/web-components/Form'
import InputEmail from '@galo-monitor/web-components/InputEmail'
import InputPhone from '@galo-monitor/web-components/InputPhone'
import InputText from '@galo-monitor/web-components/InputText'


import { Container, ModulesList } from './styles'

const CompanyForm = forwardRef((props, formRef) => {
  const { company, loading, setLoading, onSuccess, onError } = props
  const [modulesCompany, setModulesCompany] = useState([])
  const [modulesFarm, setModulesFarm] = useState([])
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: modulesCompanyData } = useGaloMonitorApi('/admin/companies/modules/companies')
  const { data: modulesFarmData } = useGaloMonitorApi('/admin/companies/modules/farms')

  const { data: companyFormData, isValidating: isValidatingFormData } = useGaloMonitorApi(!isEmpty(company) && `/admin/companies/${ company.id }`)

  const modulesCompanyOptions = useMemo(
    () => map(modulesCompanyData, app => ({ value: app.id, label: app.name })),
    [modulesCompanyData]
  )

  const modulesFarmOptions = useMemo(
    () => map(modulesFarmData, app => ({ value: app.id, label: app.name })),
    [modulesFarmData]
  )

  const findElement = useCallback((collection, value) => find(collection, item => item.id === value), [])

  const validateSameEmail = useCallback(
    () => reloadSchema(formRef, true),
    [formRef]
  )

  const handleSubmit = useCallback(
    ({ phone, ...params }) => {
      setLoading(true)
      const creatingParams = {
        modules_companies: map(modulesCompany, mod => ({ id: mod.id })),
        modules_farms: map(modulesFarm, mod => ({ id: mod.id }))
      }

      dispatch(CompanyActions.saveCompany(
        {
          ...company,
          ...params,
          ...creatingParams,
          phone: trimMask(phone)
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('company', { howMany: 1 }),
            action: toLower(t(isEmpty(company) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [setLoading, company, modulesCompany, modulesFarm, dispatch, onError, snackbar, t, onSuccess]
  )

  const schemaConstructor = useCallback(
    (...params) => companySchema(...params, { company }),
    [company]
  )

  useEffect(() => {
    if (!isValidatingFormData && !isEmpty(companyFormData)) {
      const { modulesCompanies, modulesFarms } = companyFormData

      if (!isEmpty(modulesCompanies)) {
        setModulesCompany(map(modulesCompanies, modCompany => ({ id: modCompany.id })))
      }

      if (!isEmpty(modulesFarms)) {
        setModulesFarm(map(modulesFarms, modFarm => ({ id: modFarm.id })))
      }
    }
  }, [companyFormData, isValidatingFormData])

  useEffect(
    () => {
      if (company.id) {
        validateSameEmail()
      }
    },
    [company.id, validateSameEmail]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ schemaConstructor }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 }>
            <InputText
              name="name_company"
              label={ t('name') }
              defaultValue={ company.nameCompany }
              disabled={ loading }
              fullWidth
            />
          </Grid>

          <Grid item xs={ 12 } md={ 6 }>
            <InputText
              name="name_responsible"
              label={ t('responsible (focal point)') }
              defaultValue={ company.nameResponsible }
              disabled={ loading }
              fullWidth
            />
          </Grid>

          <Grid item xs={ 12 } md={ 6 }>
            <InputPhone
              name="phone_responsible"
              label={ t('phone') }
              defaultValue={ company.phoneResponsible }
              disabled={ loading }
              fullWidth
            />
          </Grid>

          <Grid item xs={ 12 } md={ 6 }>
            <InputEmail
              name="email_responsible"
              label={ t('email') }
              defaultValue={ company.emailResponsible }
              onChange={ validateSameEmail }
              disabled={ loading }
              fullWidth
            />
          </Grid>

          <Grid item xs={ 12 } md={ 6 }>
            <InputEmail
              name="repeat_email_responsible"
              label={ t('repeat {this}', { this: toLower(t('email')) }) }
              defaultValue={ company.emailResponsible }
              onChange={ validateSameEmail }
              disabled={ loading }
              fullWidth
            />
          </Grid>

          <Grid item xs={ 6 }>
            <Typography variant="h6">
              {t('{this} modules', { this: 'Company' })}
            </Typography>
            <ModulesList container>
              {isEmpty(modulesCompanyOptions)
                ? <Grid item xs={ 12 }><I18n>empty select options</I18n></Grid>
                : map(modulesCompanyOptions, modOptions => {
                  const { value, label } = modOptions

                  return (
                    <Grid key={ uuid().new() } item xs={ 12 }>
                      <Checkbox
                        detached
                        id={ uuid().new() }
                        label={ label }
                        color="primary"
                        disabled={ isValidatingFormData }
                        checked={ !!findElement(modulesCompany, value) }
                        onClick={ () => {
                          if (findElement(modulesCompany, value))
                            setModulesCompany(prevState => filter(prevState, item => item.id !== value))
                          else
                            setModulesCompany(prevState => [...prevState, { id: value }])
                        } }
                      />
                    </Grid>
                  )
                })}
            </ModulesList>
          </Grid>

          <Grid item xs={ 6 }>
            <Typography variant="h6">
              {t('{this} modules', { this: 'Farm' })}
            </Typography>
            <ModulesList container>
              {isEmpty(modulesFarmOptions)
                ? <Grid item xs={ 12 }><I18n>empty select options</I18n></Grid>
                : map(modulesFarmOptions, modOptions => {
                  const { value, label } = modOptions

                  return (
                    <Grid key={ uuid().new() } item xs={ 12 }>
                      <Checkbox
                        detached
                        id={ uuid().new() }
                        label={ label }
                        color="primary"
                        disabled={ isValidatingFormData }
                        checked={ !!findElement(modulesFarm, value) }
                        onClick={ () => {
                          if (findElement(modulesFarm, value))
                            setModulesFarm(prevState => filter(prevState, item => item.id !== value))
                          else
                            setModulesFarm(prevState => [...prevState, { id: value }])
                        } }
                      />
                    </Grid>
                  )
                })}
            </ModulesList>
          </Grid>

        </Grid>
      </Container>
    </Form>
  )
})

CompanyForm.propTypes = {
  company: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

CompanyForm.defaultProps = {
  company: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default CompanyForm
